import { isUserGeneratedKey } from "src/scenes/orgAdmin/formTemplates/ContentTab/V3/autoGenerateKey";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import * as QuestionType from "../QuestionType";

const AnswerOptionSchema = z.object({
  id: z.string(),
  label: z.string().trim().min(1, "This field is required"),
  value: z.string().optional(), // make optional to support Grades
  userGeneratedValue: z.boolean(),
  isArchived: z.boolean().optional(),
});
export type AnswerOptionFormikType = z.infer<typeof AnswerOptionSchema>;

export const MAX_OPTIONS = 500;

export const FormikSchema = z
  .object({
    options: z
      .array(AnswerOptionSchema)
      .max(MAX_OPTIONS, `Exceeded maximum limit of ${MAX_OPTIONS} answers`),
  })
  .and(QuestionType.FormikSchema)
  .superRefine(({ options, type }, ctx) => {
    if (type !== "SingleSelect" && type !== "MultiSelect") {
      return;
    }

    if (options.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "At least 1 answer is required",
      });
    }

    // See comment below before changing the order of this validation
    options.forEach((option, index) => {
      if (option.value === undefined || option.value.trim() === "") {
        ctx.addIssue({
          path: ["options", index, "value"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
    });

    // This validation has to be evaluated after validating over options since the issue path will collide with their path.
    // e.g. {"options": {"0": {"label": "This field is required"}}} vs {"options": "Exceeded maximum limit of 500 answers"}
    if (options.length > MAX_OPTIONS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "Exceeded maximum limit of 500 answers",
      });
    }
  });

export type FormikType = z.infer<typeof FormikSchema>;

export function toFormValue(question: AF.V3.Question): FormikType {
  const options: AnswerOptionFormikType[] =
    "options" in question ? question.options.map(toAnswerOptionFormValue) : [];

  return {
    ...QuestionType.toFormValue(question),
    options,
  };
}

function toAnswerOptionFormValue(option: AF.V3.Option): AnswerOptionFormikType {
  const userGeneratedValue = isUserGeneratedKey(option.value, option.label);

  const formValue: AnswerOptionFormikType = {
    id: option.id,
    label: option.label,
    value: option.value ?? "",
    userGeneratedValue,
    isArchived: option.isArchived,
  };

  return formValue;
}
